import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { PageCard } from '@/components/ui/page-card'
import { Text } from '@/components/ui/text'
import type { UpgradeFailureReason } from '@/lib/auth-flow'
import { generateTitle } from '@/lib/utils'

// Claim the guest progress is unchanged only when the server verified it (a conflict).
const copy = {
  conflict: {
    title: 'Progress Not Linked',
    description: 'Your guest progress is unchanged.',
    body: 'This account already has progress for this game. Return to the game and try again with another email.',
  },
  alreadySaved: {
    title: 'Progress Already Saved',
    description: 'Your progress is already saved.',
    body: 'Return to the game.',
  },
  // This link can't finish, but another attempt may have: e.g. it expired, was replaced or
  // canceled, or was opened without the guest's session.
  invalid: {
    title: 'Progress Not Linked',
    description: 'This link can no longer be used.',
    body: 'Return to the game to check your progress or start a new attempt.',
  },
} satisfies Record<UpgradeFailureReason, unknown>

export const Route = createFileRoute('/upgrade-failed')({
  validateSearch: z.object({ reason: z.enum(['conflict', 'alreadySaved', 'invalid']).optional() }),
  component: UpgradeFailed,
  head: ({ match }) => ({
    meta: [{ title: generateTitle(copy[match.search.reason ?? 'invalid'].title) }],
  }),
})

function UpgradeFailed() {
  const { reason } = Route.useSearch()
  const { title, description, body } = copy[reason ?? 'invalid']
  return (
    <PageCard title={title} description={description}>
      <Text block size={14}>
        {body}
      </Text>
      <Button flex className="mt-8 w-full" onClick={() => window.close()}>
        Close and Return to Game
      </Button>
    </PageCard>
  )
}
